/**
 * Inline Text Component for Timeline V2
 *
 * Renders inline text with bold emphasis for actors, field names, and values.
 *
 * Pattern: "**Actor** action **field** to **value**"
 * Example: "John Doe updated First Name to Jane"
 *
 * Usage:
 * ```tsx
 * <InlineText>
 *   {bold('John Doe')} updated {bold('First Name')} to {bold('Jane')}
 * </InlineText>
 * ```
 */

import type { ReactNode } from 'react';

import { cx } from '@/utils/cx';

import { TIMELINE_DESIGN_TOKENS } from '../../design-tokens';

const INLINE_EMPHASIS_LINK_CLASS = cx(
  TIMELINE_DESIGN_TOKENS.typography.emphasis,
  TIMELINE_DESIGN_TOKENS.states.linkHover,
  'text-fg-brand-primary'
);

export interface InlineTextProps {
  children: ReactNode;
  className?: string;
}

/**
 * Container for inline event text
 */
export function InlineText({ children, className = '' }: InlineTextProps) {
  return (
    <p
      className={`${TIMELINE_DESIGN_TOKENS.typography.eventText} ${TIMELINE_DESIGN_TOKENS.colors.eventText} leading-5 ${className}`}
    >
      {children}
    </p>
  );
}

/**
 * Helper to render bold text segments
 *
 * Use for: actor names, field names, values
 */
// eslint-disable-next-line react-refresh/only-export-components -- helper function co-located with InlineText component
export function bold(text: ReactNode): ReactNode {
  return (
    <span className={TIMELINE_DESIGN_TOKENS.typography.emphasis}>{text}</span>
  );
}

/**
 * Helper to render link segments
 */
// eslint-disable-next-line react-refresh/only-export-components -- helper function co-located with InlineText component
export function link(
  text: ReactNode,
  href: string,
  target: '_blank' | '_self' = '_blank'
): ReactNode {
  return (
    <a
      href={href}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      className={INLINE_EMPHASIS_LINK_CLASS}
    >
      {text}
    </a>
  );
}

/**
 * Helper to render an inline button segment — same visual treatment as `link()`,
 * but triggers an action instead of navigating. Used for things like clicking a
 * thread subject inside a timeline event to open the thread slideout.
 */
// eslint-disable-next-line react-refresh/only-export-components -- helper function co-located with InlineText component
export function inlineButton(
  text: ReactNode,
  onClick: () => void,
  options: { ariaLabel?: string } = {}
): ReactNode {
  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={options.ariaLabel}
      className={cx(
        INLINE_EMPHASIS_LINK_CLASS,
        TIMELINE_DESIGN_TOKENS.states.focusRing
      )}
    >
      {text}
    </button>
  );
}
